import gsap from "gsap"

export default class Hero {
    constructor(options = {}) {
        this.navigation = document.querySelector(".hero__content__right__navigation")
        this.slides = this.navigation?.querySelectorAll("a")
        this.videos = document.querySelectorAll(".hero__image[data-video-index]")
        this.thumbMedia = document.querySelectorAll(".hero__content__right__navigation .thumb video, .hero__content__right__navigation .thumb img")
        this.timer = this.navigation?.querySelector(".timer")
        this.mobileNavigation = document.querySelector(".hero__content__left__navigation")
        this.mobilePrev = this.mobileNavigation?.querySelector(".prev")
        this.mobileNext = this.mobileNavigation?.querySelector(".next")
        this.titleSlides = document.querySelectorAll(".hero__title__slide")
        this.currentIndex = 0
        this.autoplayInterval = null
        this.autoplayDelay = 15000
        this.isDesktop = window.innerWidth > 1024
        this.currentTimerAnimation = null
        this.titleAnimating = false
        
        this.init()
        this.initTitles()
        this.handleResize()
        this.handleVisibilityChange()
    }

    init() {
        if (!this.slides || this.slides.length === 0) return
        
        this.videos.forEach((video, i) => {
            if (i === 0) {
                gsap.set(video, { opacity: 0.7, zIndex: 1 })
                if (video.tagName === 'VIDEO') {
                    video.play()
                }
            } else {
                gsap.set(video, { opacity: 0, zIndex: 0 })
                if (video.tagName === 'VIDEO') {
                    video.pause()
                }
            }
        })
        
        // Thumbnails remain static - no play/pause needed
        
        if (this.isDesktop) {
            this.setActiveSlide(0)
        }
        
        this.slides.forEach((slide, index) => {
            slide.addEventListener("click", (e) => {
                e.preventDefault()
                if (this.isDesktop) {
                    this.goToSlide(index)
                }
            })
        })
        
        if (this.mobilePrev) {
            this.mobilePrev.addEventListener("click", (e) => {
                e.preventDefault()
                this.goToPrevSlide()
            })
        }
        
        if (this.mobileNext) {
            this.mobileNext.addEventListener("click", (e) => {
                e.preventDefault()
                this.goToNextSlide()
            })
        }
        
        if (this.isDesktop) {
            this.startAutoplay()
        }
    }

    initTitles() {
        if (!this.titleSlides || this.titleSlides.length === 0) return

        this.titleSlides.forEach((titleSlide, i) => {
            if (i === 0) {
                titleSlide.classList.add("active")
                gsap.set(titleSlide, { opacity: 1, y: 0 })
            } else {
                titleSlide.classList.remove("active")
                gsap.set(titleSlide, { opacity: 0, y: 30 })
            }
        })
    }

    animateTitle(fromIndex, toIndex) {
        if (this.titleAnimating || !this.titleSlides.length) return
        if (fromIndex === toIndex) return
        
        this.titleAnimating = true
        
        const fromTitle = this.titleSlides[fromIndex]
        const toTitle = this.titleSlides[toIndex]
        
        if (!fromTitle || !toTitle) {
            this.titleAnimating = false
            return
        }
        
        const timeline = gsap.timeline({
            onComplete: () => {
                this.titleAnimating = false
            }
        })
        
        // Fade out current title
        timeline.to(fromTitle, {
            opacity: 0,
            y: -30,
            duration: 0.5,
            ease: "power2.in",
            onComplete: () => {
                fromTitle.classList.remove("active")
            }
        })
        
        // Fade in new title
        timeline.call(() => {
            toTitle.classList.add("active")
            gsap.set(toTitle, { opacity: 0, y: 30 })
        })
        
        timeline.to(toTitle, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out"
        })
    }

    handleResize() {
        window.addEventListener("resize", () => {
            const wasDesktop = this.isDesktop
            this.isDesktop = window.innerWidth > 1024
            
            if (!wasDesktop && this.isDesktop) {
                this.setActiveSlide(0)
                this.startAutoplay()
            }
            
            if (wasDesktop && !this.isDesktop) {
                this.stopAutoplay()
                this.slides.forEach(slide => {
                    slide.classList.remove("active")
                    const span = slide.querySelector("span")
                    gsap.set(span, { clearProps: "all" })
                    gsap.set(slide, { clearProps: "all" })
                })
            }
        })
    }

    handleVisibilityChange() {
        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.stopAutoplay()
            } else {
                if (this.isDesktop) {
                    this.slides.forEach((slide, i) => {
                        const span = slide.querySelector("span")
                        if (i === this.currentIndex) {
                            gsap.set(span, { opacity: 1, x: 0 })
                            gsap.set(slide, { width: "27.7rem", flex: "1 1 27.7rem" })
                        } else {
                            gsap.set(span, { opacity: 0, x: -20 })
                            gsap.set(slide, { width: "13.4rem", flex: "0 0 13.4rem" })
                        }
                    })
                    
                    this.startAutoplay()
                    
                    if (this.timer) {
                        gsap.set(this.timer, { width: "0%" })
                        this.animateTimer(this.timer)
                    }
                }
            }
        })
    }

    goToSlide(index) {
        if (index === this.currentIndex) return
        
        const previousIndex = this.currentIndex
        this.currentIndex = index
        
        const previousSlide = this.slides[previousIndex]
        const previousSpan = previousSlide.querySelector("span")
        const currentSlide = this.slides[index]
        const currentSpan = currentSlide.querySelector("span")
        
        const timeline = gsap.timeline()
        
        previousSlide.classList.remove("active")
        currentSlide.classList.add("active")
        
        if (this.currentTimerAnimation) {
            this.currentTimerAnimation.kill()
        }
        if (this.timer) {
            gsap.set(this.timer, { width: "0%" })
        }
        
        timeline.to(previousSpan, {
            opacity: 0,
            x: -20,
            duration: 0.3,
            ease: "power2.in"
        }, 0)
        
        timeline.to(previousSlide, {
            width: "13.4rem",
            flex: "0 0 13.4rem",
            duration: 0.6,
            ease: "power3.inOut"
        }, 0.15)
        
        timeline.to(currentSlide, {
            width: "27.7rem",
            flex: "1 1 27.7rem",
            duration: 0.6,
            ease: "power3.inOut"
        }, 0.15)
        
        timeline.fromTo(currentSpan, 
            {
                opacity: 0,
                x: -20
            },
            {
                opacity: 1,
                x: 0,
                duration: 0.4,
                ease: "power2.out"
            },
            1.1
        )
        
        this.stopAutoplay()
        this.startAutoplay()
        this.animateTimer(this.timer)
        
        this.crossfadeVideo(index)
        this.animateTitle(previousIndex, index)
    }

    goToNextSlide() {
        const previousIndex = this.currentIndex
        const nextIndex = (this.currentIndex + 1) % this.slides.length
        
        if (!this.isDesktop) {
            this.currentIndex = nextIndex
            this.crossfadeVideo(nextIndex)
            this.animateTitle(previousIndex, nextIndex)
        } else {
            this.goToSlide(nextIndex)
        }
    }

    goToPrevSlide() {
        const previousIndex = this.currentIndex
        const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length
        
        if (!this.isDesktop) {
            this.currentIndex = prevIndex
            this.crossfadeVideo(prevIndex)
            this.animateTitle(previousIndex, prevIndex)
        } else {
            this.goToSlide(prevIndex)
        }
    }

    animateSlideOut(index) {
        const slide = this.slides[index]
        const span = slide.querySelector("span")
        
        slide.classList.remove("active")
        
        gsap.to(span, {
            opacity: 0,
            x: -20,
            duration: 0.3,
            ease: "power2.in"
        })
        
        gsap.to(slide, {
            flex: "0 0 13.4rem",
            duration: 0.6,
            ease: "power3.inOut",
            delay: 0.15
        })
    }

    animateSlideIn(index) {
        const slide = this.slides[index]
        const span = slide.querySelector("span")
        
        slide.classList.add("active")
        
        gsap.to(slide, {
            flex: "1 1 27.7rem",
            duration: 0.6,
            ease: "power3.inOut",
            onComplete: () => {
                gsap.fromTo(span, 
                    {
                        opacity: 0,
                        x: -20
                    },
                    {
                        opacity: 1,
                        x: 0,
                        duration: 0.4,
                        ease: "power2.out"
                    }
                )
            }
        })
    }

    setActiveSlide(index) {
        this.slides.forEach((slide, i) => {
            const span = slide.querySelector("span")
            
            if (i === index) {
                slide.classList.add("active")
                gsap.set(span, { opacity: 1, x: 0 })
                gsap.set(slide, { width: "27.7rem", flex: "1 1 27.7rem" })
            } else {
                slide.classList.remove("active")
                gsap.set(span, { opacity: 0, x: -20 })
                gsap.set(slide, { width: "13.4rem", flex: "0 0 13.4rem" })
            }
        })
        
        if (this.timer) {
            gsap.set(this.timer, { width: "0%" })
            this.animateTimer(this.timer)
        }
    }

    animateTimer(timer) {
        if (!timer) return
        
        if (this.currentTimerAnimation) {
            this.currentTimerAnimation.kill()
        }
        
        this.currentTimerAnimation = gsap.fromTo(timer, 
            { width: "0%" },
            { 
                width: "100%", 
                duration: this.autoplayDelay / 1000,
                ease: "linear",
                onComplete: () => {
                    if (this.isDesktop && this.autoplayInterval) {
                        const nextIndex = (this.currentIndex + 1) % this.slides.length
                        this.goToSlide(nextIndex)
                    }
                }
            }
        )
    }

    crossfadeVideo(index) {
        const currentVideo = this.videos[index]
        
        this.videos.forEach((video, i) => {
            if (i === index) {
                gsap.to(video, {
                    opacity: 0.7,
                    duration: 1.2,
                    ease: "power2.inOut",
                    onStart: () => {
                        gsap.set(video, { zIndex: 1 })
                        if (video.tagName === 'VIDEO') {
                            video.play()
                        }
                    }
                })
            } else {
                gsap.to(video, {
                    opacity: 0,
                    duration: 1.2,
                    ease: "power2.inOut",
                    onComplete: () => {
                        gsap.set(video, { zIndex: 0 })
                        if (video.tagName === 'VIDEO') {
                            video.pause()
                            video.currentTime = 0
                        }
                    }
                })
            }
        })
        
        // Thumbnails remain static - no play/pause needed
    }

    startAutoplay() {
        this.autoplayInterval = true
    }

    stopAutoplay() {
        this.autoplayInterval = false
        if (this.currentTimerAnimation) {
            this.currentTimerAnimation.kill()
        }
    }

    destroy() {
        this.stopAutoplay()
    }
}

